import React, { useState, useEffect, useCallback } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Star, ChevronDown, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ReviewContent from '@/pages/Module2/ReviewContent'

interface UserReviewListProps {
  organisationId: number
  userId: number
  rolePlayId: number
}

export interface ReviewEntry {
  _id: string
  rolePlayName: string
  rolePlayId: number
  score: number
  mode: string | null
  feedback: string
  mastered: string | null
  toImprove: string | null
  sessionToken: string | null
  createdAt: string
}

const GET_ORGANISATION_USER_REVIEWS = gql`
  query getOrganisationUserReviews($organisationId: Int!, $userId: Int!, $rolePlayId: Int, $limit: Int, $offset: Int) {
    getOrganisationUserReviews(organisationId: $organisationId, userId: $userId, rolePlayId: $rolePlayId, limit: $limit, offset: $offset) {
      _id
      rolePlayName
      rolePlayId
      score
      mode
      feedback
      mastered
      toImprove
      sessionToken
      createdAt
    }
  }
`

const PAGE_SIZE = 10

const formatDate = (isoString: string): string => {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return isoString
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const truncate = (text: string, max: number): string => (text.length > max ? `${text.slice(0, max).trimEnd()}…` : text)

// Level 3 of the User Targets tab: one user's reviews for one role play, newest first,
// each expandable to the same ReviewContent the learner sees on their Review page.
const UserReviewList: React.FC<UserReviewListProps> = ({ organisationId, userId, rolePlayId }) => {
  const [reviews, setReviews] = useState<ReviewEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const fetchPage = useCallback(
    async (offset: number): Promise<ReviewEntry[]> => {
      const response = await authorizedGraphQLRequest<{ getOrganisationUserReviews: ReviewEntry[] }>(GET_ORGANISATION_USER_REVIEWS, {
        organisationId,
        userId,
        rolePlayId,
        limit: PAGE_SIZE,
        offset,
      })
      return response.getOrganisationUserReviews || []
    },
    [organisationId, userId, rolePlayId],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    setExpandedId(null)
    fetchPage(0)
      .then((rows) => {
        if (cancelled) return
        setReviews(rows)
        setHasMore(rows.length === PAGE_SIZE)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to fetch user reviews:', err)
        setError(true)
        setReviews([])
        setHasMore(false)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fetchPage])

  const handleLoadMore = async () => {
    if (loadingMore) return
    setLoadingMore(true)
    try {
      const rows = await fetchPage(reviews.length)
      setReviews((prev) => [...prev, ...rows])
      setHasMore(rows.length === PAGE_SIZE)
    } catch (err) {
      console.error('Failed to fetch more user reviews:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return <div className="text-center text-red-600 py-8 bg-red-50 rounded-xl">Failed to load sessions</div>
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
        <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p>No sessions with feedback yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {reviews.map((entry) => {
          const isExpanded = expandedId === entry._id
          return (
            <div key={entry._id} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden" data-testid="review-entry">
              <div className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:gap-4">
                <div className="flex flex-wrap items-center gap-2 md:w-[32%]">
                  <span className="inline-flex items-center rounded-full border border-gray-100 bg-gray-50 px-3 py-1 text-[11px] font-medium text-gray-600">{formatDate(entry.createdAt)}</span>
                  {entry.mode && <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[11px] font-medium text-primary">{entry.mode}</span>}
                  <span className="inline-flex items-center gap-1 rounded-full border border-gray-100 bg-gray-50 px-3 py-1 text-[11px] font-medium text-gray-600">
                    <Star size={12} className="fill-yellow-400 text-yellow-400" />
                    {entry.score}/5
                  </span>
                </div>

                <p className="min-w-0 md:flex-1 text-xs text-gray-600 line-clamp-2 md:line-clamp-1" title={entry.feedback}>
                  {truncate(entry.feedback, 140)}
                </p>

                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" onClick={() => setExpandedId(isExpanded ? null : entry._id)} aria-expanded={isExpanded}>
                    {isExpanded ? 'Hide' : 'Details'}
                    <ChevronDown size={14} className={`ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4">
                  <ReviewContent mode={entry.mode} score={entry.score} createdAt={entry.createdAt} mastered={entry.mastered} toImprove={entry.toImprove} feedback={entry.feedback} />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'View More'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default UserReviewList
